import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { InputMask } from "primereact/inputmask";

export const Container = styled.div`
  padding: 12px 32px;
  display: flex;
  flex-direction: column;
`;

export const TopRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export const Breadcrumbs = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: #181818;
`;

export const BreadcrumbLink = styled(Link)`
  color: #181818;
  text-decoration: none;

  &:hover {
    color: #065fa6;
    text-decoration: underline;
  }
`;

export const ExitAppBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: none;
  padding: 4px 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: #181818;
  cursor: pointer;

  &:hover {
    color: #065fa6;
  }
`;

interface ISearchControls {
  $isResults: boolean;
}

export const SearchControls = styled.div<ISearchControls>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  margin: auto;
  margin-top: 130px;

  ${({ $isResults }) =>
    $isResults &&
    `
    margin-top: 40px;
  `}
`;

export const Title = styled.h1`
  margin: 0;
  color: #181818;
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  text-align: center;
`;

export const FormSelector = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-self: center;
  margin-top: 12px;
`;

export const FormSelectorOption = styled.button<{ $active?: boolean }>`
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  border: none;
  background: none;
  text-align: center;
  padding: 6px 16px;
  border-radius: 100px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? "#065FA6" : "#181818")};
  outline: 1px solid ${({ $active }) => ($active ? "#B7D6EE" : "#e7e7e7")};
  background: ${({ $active }) => ($active ? "#E8F2FB" : "transparent")};

  &:hover {
    outline-color: ${({ $active }) => ($active ? "#B7D6EE" : "#7c7f7e")};
  }
`;

export const SearchFormOuter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 40px;
`;

export const SearchForm = styled.div`
  width: 100%;
  max-width: 680px;
`;

export const Form = styled.form`
  position: relative;
`;

export const FormLayout = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-bottom: 10px;
`;

export const FieldsGrid = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

export const FieldCell = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #181818;
  margin-bottom: 6px;
`;

/* Aligns the inline Search button with inputs that have labels above them */
export const InlineBtnWrap = styled.div`
  padding-top: 26px;
  align-self: flex-start;
`;

export const BelowBtnWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 56px;
`;

export const FieldControls = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 24px;
`;

export const ControlItem = styled.button`
  font-family: inherit;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  background: none;
  border: 1px solid gray;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  gap: 6px;
  color: ${({ theme }) => theme.searchPatients.input.text};
  cursor: pointer;

  &:hover {
    border-color: #065fa6;
    color: #065fa6;
  }
`;

export const SubmitBtn = styled.button`
  background: #065fa6;
  color: white;
  font-size: 16px;
  padding: 12.8px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  min-width: 115px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: #181818;
    transition: background-color 250ms linear;
  }
`;

export const GlobalError = styled.div`
  color: #850000;
  font-size: 12px;
  margin-bottom: 16px;
`;

export const InputWrap = styled.div`
  position: relative;
`;

export const RemoveFieldBtn = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
`;

interface IInputProps {
  $hasError?: boolean;
  $hasRemove?: boolean;
}

export const Input = styled.input<IInputProps>`
  ${({ theme }) => theme.fields.SEARCH}
  width: 100%;
  background-color: ${({ theme }) => theme.searchPatients.input.bg};
  color: ${({ theme }) => theme.searchPatients.input.text} !important;
  border: none;
  ${({ $hasRemove }) => $hasRemove && "padding-right: 44px;"}
  ${({ $hasError }) => $hasError && "outline: 1px solid #850000;"}
`;

export const MaskedInput = styled(InputMask)<IInputProps>`
  ${({ theme }) => theme.fields.SEARCH}
  width: 100%;
  background-color: ${({ theme }) => theme.searchPatients.input.bg};
  color: ${({ theme }) => theme.searchPatients.input.text} !important;
  border: none;
  ${({ $hasRemove }) => $hasRemove && "padding-right: 44px;"}
  ${({ $hasError }) => $hasError && "outline: 1px solid #850000;"}
`;

export const Error = styled.div`
  position: absolute;
  top: 100%;
  color: #850000;
  font-size: 12px;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: 40px auto;
  color: #5a5959;
  font-size: 16px;
  line-height: 22px;
`;

export const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #e7e7e7;
  border-top-color: #065fa6;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const NoResults = styled.div`
  margin: 24px auto 40px;
  text-align: center;
  color: #5a5959;
  font-size: 16px;
  line-height: 22px;
`;

export const RetryBtn = styled.button`
  margin-top: 12px;
  padding: 8px 20px;
  border: 1px solid #065fa6;
  border-radius: 20px;
  background: transparent;
  color: #065fa6;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    background: #eff7ff;
  }
`;

export const ResultsHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  width: 100%;
  max-width: 1376px;
  margin: auto;
`;
